import React from 'react';
import { Paper, Text, Badge, Group, Box, useMantineTheme, useComputedColorScheme } from '@mantine/core';
import { IconStar, IconSparkles } from '@tabler/icons-react';
import { IngredientAnalysis } from '../../types/menu';
import { t } from '../../utils/uiTranslations';

interface IngredientHighlightsProps {
  ingredients: IngredientAnalysis[];
  currentLanguage?: string;
  /** canonical_name of the ingredient to visually focus/highlight */
  focusedIngredient?: string | null;
}

/** Capitalize each word for display (e.g. "misuji beef" → "Misuji Beef") */
function toTitleCase(s: string): string {
  if (!s?.trim()) return s;
  return s.trim().replace(/\w\S*/g, (w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase());
}

/**
 * Component that highlights uncommon and rare ingredients in a menu item.
 * Uncommon ingredients are listed without a badge; rare/very_rare show a "Special" badge.
 * Descriptions and attributes (origin, brand) are shown for all displayed ingredients.
 */
const RARITY_RANK: Record<string, number> = { very_rare: 3, rare: 2, uncommon: 1, common: 0 };

export const IngredientHighlights: React.FC<IngredientHighlightsProps> = ({
  ingredients,
  currentLanguage = 'en',
  focusedIngredient = null
}) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';

  const specialIngredients = ingredients.filter(ingredient => ingredient.display === true);

  if (specialIngredients.length === 0) {
    return null;
  }

  // Show only the focused ingredient, or fall back to the rarest one
  const activeIngredient = focusedIngredient
    ? (specialIngredients.find(i =>
        i.canonical_name === focusedIngredient ||
        i.name_en === focusedIngredient ||
        i.name_jp === focusedIngredient
      ) ?? specialIngredients.slice().sort((a, b) => (RARITY_RANK[b.rarity] ?? 0) - (RARITY_RANK[a.rarity] ?? 0))[0])
    : specialIngredients.slice().sort((a, b) => (RARITY_RANK[b.rarity] ?? 0) - (RARITY_RANK[a.rarity] ?? 0))[0];

  if (!activeIngredient) return null;

  // Get rarity badge color and icon
  const getRarityConfig = (rarity: string) => {
    switch (rarity) {
      case 'uncommon':
        return {
          color: 'green',
          icon: <IconStar size={12} />,
          label: t('rarityUncommon', currentLanguage)
        };
      case 'rare':
        return {
          color: 'orange',
          icon: <IconSparkles size={12} />,
          label: t('rarityRare', currentLanguage)
        };
      case 'very_rare':
        return {
          color: 'red',
          icon: <IconSparkles size={12} />,
          label: t('rarityVeryRare', currentLanguage)
        };
      default:
        return {
          color: 'gray',
          icon: null,
          label: t('raritySpecial', currentLanguage)
        };
    }
  };

  const rarityConfig = getRarityConfig(activeIngredient.rarity);
  const ingredientName = currentLanguage === 'jp'
    ? (activeIngredient.name_jp || activeIngredient.name || activeIngredient.canonical_name || '')
    : (activeIngredient.name_en || activeIngredient.name || activeIngredient.canonical_name || '');
  const ingredientDescription = currentLanguage === 'jp'
    ? (activeIngredient.description_jp || activeIngredient.description || '')
    : (activeIngredient.description_en || activeIngredient.description || '');
  const showBadge = activeIngredient.rarity !== 'uncommon';
  const displayName = toTitleCase(ingredientName);

  return (
    <Paper
      withBorder
      p="md"
      radius="md"
      style={{
        backgroundColor: isDarkMode ? theme.colors.dark[6] : theme.colors.gray[0]
      }}
    >
      <Box style={{ padding: '4px 8px' }}>
        <Group gap="xs">
          <Text size="sm" fw={500}>{displayName}</Text>
          {showBadge && (
            <Badge size="xs" color={rarityConfig.color} variant="light" leftSection={rarityConfig.icon}>
              {rarityConfig.label}
            </Badge>
          )}
        </Group>

        {ingredientDescription && (
          <Text size="xs" style={{ lineHeight: 1.4 }} mt={2}>
            {ingredientDescription}
          </Text>
        )}
        {activeIngredient.attributes?.brand && (
          <Group gap="xs" mt={2}>
            <Text size="xs">
              {t('ingredientBrandLabel', currentLanguage)}: {activeIngredient.attributes.brand}
            </Text>
          </Group>
        )}
      </Box>
    </Paper>
  );
};
